import { useContext, useMemo } from 'react';
import { isQuickFilterEditorSchema, isUserAddedQuickFilterEditorSchema } from 'reporting-data/tsTypes/editorTypes';
import { UserInfoContext } from 'reporting-data/userInfo/userInfoContext';
import GoalsKPIUnitTypeEditor from '../GoalEditor/GoalsKPIUnitTypeEditor';
import { getWidgetEditors } from '../getWidgetEditors';
import { editorMatchesCategory, getEditorSchemasFromConfig } from './editorUtils';
import { useDynamicallyGeneratedEditorSchemas, useDynamicallyGeneratedReportEditors, useEditorComponents, useEditorsFromConfig } from './useEditorComponents';
export const useEditorsAndFilters = (report, previewReport, editorOverrides) => {
  const {
    gates = [],
    user: {
      scopes = []
    }
  } = useContext(UserInfoContext);
  const dynamicFilters = useDynamicallyGeneratedReportEditors(previewReport);
  const usableWidgetEditors = getWidgetEditors(report).filter(Editor => Editor.isUsableForReport(previewReport, gates, scopes));
  const editorsFromConfig = useEditorsFromConfig(report);
  return useMemo(() => {
    const dateEditors = usableWidgetEditors.filter(editor => editor.isDateEditor);
    const remainingEditors = usableWidgetEditors.filter(editor => !editor.isDateEditor);
    return editorOverrides || [...dateEditors, ...editorsFromConfig, ...dynamicFilters, ...remainingEditors];
  }, [dynamicFilters, editorOverrides, editorsFromConfig, usableWidgetEditors]);
};
export const useFilters = report => {
  const {
    gates,
    user: {
      scopes
    }
  } = useContext(UserInfoContext);
  // Set of static editors that render before the Filters Bar
  const filterEditors = [...getWidgetEditors(report), GoalsKPIUnitTypeEditor].filter(Editor => Editor.isUsableForReport(report, gates, scopes) && Editor.isFilterEditor);
  const dynamicFiltersSchema = useDynamicallyGeneratedEditorSchemas(report);
  const editorSchemaFromConfig = getEditorSchemasFromConfig(report);
  const filterSchema = [...dynamicFiltersSchema, ...editorSchemaFromConfig];
  return {
    filterEditors,
    filterProperties: filterSchema.reduce((memo, schema) => {
      if (isQuickFilterEditorSchema(schema)) {
        memo.push(schema.property);
      }
      return memo;
    }, Array()),
    userAddedFilterProperties: filterSchema.reduce((memo, schema) => {
      if (isUserAddedQuickFilterEditorSchema(schema)) {
        memo.push(schema.property);
      }
      return memo;
    }, Array())
  };
};
export const useEditors = (report, category) => {
  const {
    gates,
    user: {
      scopes
    }
  } = useContext(UserInfoContext);
  const editors = getWidgetEditors(report).filter(Editor => Editor.isUsableForReport(report, gates, scopes) && !Editor.isFilterEditor && editorMatchesCategory(Editor, category));
  const dynamicFiltersSchema = useDynamicallyGeneratedEditorSchemas(report);
  const editorSchemaFromConfig = getEditorSchemasFromConfig(report);
  const editorSchema = [...dynamicFiltersSchema, ...editorSchemaFromConfig].filter(schema => !isQuickFilterEditorSchema(schema) && !isUserAddedQuickFilterEditorSchema(schema));
  const editorsWithSchema = useEditorComponents(report, editorSchema, undefined, category);
  return [...editors, ...editorsWithSchema];
};